import type { EntitlementResult } from '@/hooks/useEntitlement';
import { STRINGS } from '@/strings/strings';

/**
 * PWA #71 — Mixed-State UI Counter Gap.
 *
 * Pure function that computes whether a user is in the mixed-state
 * (subscriptionRemaining=0 AND non-sub credits available) and, if so,
 * returns the depletion + reserve subline pair to render alongside
 * the subscription counter.
 *
 * Trigger condition (Sebastian D5 lock 2026-04-30): renders only when
 *   subscriptionRemaining === 0 AND (packRemaining > 0 OR trialRemaining > 0)
 *
 * Returns `null` for every other state, including:
 *   - active sub with credits remaining (>0): subline is bonus info
 *     the user doesn't need to worry about right now (Sebastian D5)
 *   - all sources empty (totalRemaining=0): the existing trial-empty
 *     fallback / "Plan wählen" CTA path handles this state
 *   - null entitlement (pre-fetch race): defer to existing fallbacks
 *
 * Source priority when both pack and trial reserves exist (Sebastian D3
 * lock): pack wins. Pack is user-purchased and more salient than the
 * 1-trial-credit-on-signup case, which is the dominant production
 * trial state.
 */

export interface ReserveLine {
  depletionLine: string;
  reserveLine: string;
}

export function pickReserveLine(entitlement: EntitlementResult | null): ReserveLine | null {
  if (!entitlement) return null;

  const { subscriptionRemaining, packRemaining, trialRemaining } = entitlement;
  const totalRemaining = entitlement.totalRemaining;

  // Discriminator: user must have an ACTIVE subscription that's exhausted —
  // not just subscriptionRemaining=0 (trial-only users also have that). The
  // depletion line ("Diesen Monat sind alle Abo-Geschichten aufgebraucht")
  // is meaningless for users without a subscription. Per PWA #53 contract,
  // an active subscription always emits a source-array entry, even at 0
  // remaining — that's the cleanest discriminator.
  const hasActiveSubscription = entitlement.sources.some((s) => s.type === 'subscription');
  if (!hasActiveSubscription) return null;

  // Trigger condition — strict equality on subscriptionRemaining=0
  // (per Sebastian D5; do not fire when sub still has any credit).
  if (subscriptionRemaining !== 0) return null;
  if (totalRemaining === 0) return null;
  if (packRemaining <= 0 && trialRemaining <= 0) return null;

  const M = STRINGS.shared.mixedState;
  const depletionLine = M.depletionLineSubscription;

  // Pack wins over trial when both > 0 (D3 — pack is user-purchased
  // and more salient than the 1-trial-credit-on-signup case).
  if (packRemaining > 0) {
    const reserveLine =
      packRemaining === 1
        ? M.reserveLinePackOne
        : M.reserveLinePack.replace('{count}', String(packRemaining));
    return { depletionLine, reserveLine };
  }

  // trialRemaining > 0
  const reserveLine =
    trialRemaining === 1
      ? M.reserveLineTrialOne
      : M.reserveLineTrial.replace('{count}', String(trialRemaining));
  return { depletionLine, reserveLine };
}
